import React, { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";

function AssignSchools({
  assignments,
  qaUsers,
  centers,
  provinces,
  cities,
  stats,
  onFetchCities,
  onFetchCenters,
  onAddAssignment,
  onDeleteAssignment,
  priorities,
  schedules,
  onStartVisit,
  onCompleteVisit
}) {
  const [filterUser, setFilterUser] = useState("ALL");
  const { register, handleSubmit, reset, setValue, watch } = useForm();

  const watchProvince = watch("province");
  const watchCity = watch("city");

  // Set default visit date to today
  useEffect(() => {
    setValue("visitDate", new Date().toISOString().split('T')[0]);
  }, [setValue]);

  // Fetch cities when province changes
  useEffect(() => {
    if (watchProvince) {
      onFetchCities(watchProvince);
    }
    // Always clear dependent fields when province changes
    setValue("city", "");
    setValue("center", "");
  }, [watchProvince, onFetchCities, setValue]);

  // Fetch centers when city changes
  useEffect(() => {
    if (watchCity) {
      onFetchCenters(watchCity);
    }
    // Always clear center when city changes
    setValue("center", "");
  }, [watchCity, onFetchCenters, setValue]);

  // Filter assignments by selected QA user
  const filteredAssignments = filterUser === "ALL" 
    ? assignments 
    : assignments.filter(a => a.qaUserId === filterUser);

  const handleFormSubmit = async (data) => {
    const assignmentData = {
      qaUserId: data.qaUser,
      centerId: data.center,
      notes: data.notes,
      visitDate: data.visitDate,
      priority: data.priority,
      assignedBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
    };

    const success = await onAddAssignment(assignmentData);
    if (success) {
      reset();
      setValue("province", "");
      setValue("city", "");
      setValue("visitDate", new Date().toISOString().split('T')[0]);
    }
  };

  const getCenterName = useCallback((centerId, fallbackName) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.name : (fallbackName || centerId || "Unknown Center");
  }, [centers]);

  const getCityName = useCallback((centerId) => {
    const center = centers.find(c => String(c.id) === String(centerId));
    return center ? center.city : null;
  }, [centers]);

  const getUserName = useCallback((userId, fallbackName) => {
    const user = qaUsers.find(u => String(u.id) === String(userId));
    return user ? `${user.firstName} ${user.lastName}` : (fallbackName || "Unknown User");
  }, [qaUsers]);

  const getScheduleForAssignment = useCallback((qaUserId, centerId) => {
    return schedules.find(s => 
      String(s.qaUserId) === String(qaUserId) && 
      String(s.centerId) === String(centerId)
    );
  }, [schedules]);

  const getStatusBadge = (status) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      minWidth: '100px',
      justifyContent: 'center',
      whiteSpace: 'nowrap'
    };

    const styles = {
      Pending: {
        background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
        color: '#856404',
        border: '1px solid #ffd166'
      },
      "In Progress": {
        background: 'linear-gradient(135deg, #cfe2ff 0%, #a5c8ff 100%)',
        color: '#084298',
        border: '1px solid #74c0fc'
      },
      Completed: {
        background: 'linear-gradient(135deg, #d4edda 0%, #a8e6a3 100%)',
        color: '#155724',
        border: '1px solid #8fd19e'
      }
    };

    const style = styles[status] || {
      background: '#f3f5f7',
      color: '#6c757d',
      border: '1px solid #e2e8f0'
    };

    return (
      <span style={{ ...baseStyle, ...style }}>
        {status === "Pending" && <i className="fas fa-clock" style={{ fontSize: '0.7rem' }}></i>}
        {status === "In Progress" && <i className="fas fa-play-circle" style={{ fontSize: '0.7rem' }}></i>}
        {status === "Completed" && <i className="fas fa-check-circle" style={{ fontSize: '0.7rem' }}></i>}
        {status}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      minWidth: '80px',
      justifyContent: 'center',
      whiteSpace: 'nowrap'
    };

    const styles = {
      High: {
        background: '#fff5f5',
        color: '#c53030',
        border: '1px solid #feb2b2'
      },
      Medium: {
        background: '#fffaf0',
        color: '#dd6b20',
        border: '1px solid #fbd38d'
      },
      Low: {
        background: '#f0fff4',
        color: '#2f855a',
        border: '1px solid #9ae6b4'
      },
      Normal: {
        background: '#ebf8ff',
        color: '#3182ce',
        border: '1px solid #bee3f8'
      }
    };

    const style = styles[priority] || {
      background: '#f7fafc',
      color: '#718096',
      border: '1px solid #e2e8f0'
    };

    return (
      <span style={{ ...baseStyle, ...style }}>
        {priority === "High" && <i className="fas fa-exclamation-circle" style={{ fontSize: '0.7rem' }}></i>}
        {priority === "Medium" && <i className="fas fa-exclamation-triangle" style={{ fontSize: '0.7rem' }}></i>}
        {priority === "Low" && <i className="fas fa-arrow-down" style={{ fontSize: '0.7rem' }}></i>}
        {priority || "Normal"}
      </span>
    );
  };


  return (
    <div className="row g-3">
      {/* Left Column - Form & Stats */}
      <div className="col-lg-4">
        {/* Assignment Form */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-user-plus mr-2" style={{ color: '#4f46e5' }}></i>
            Assign Center to QA User
          </div>
          
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="mb-3">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.85rem',
                color: '#4a5568'
              }}>
                <i className="" style={{ fontSize: '0.75rem' }}></i>
                QA User <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                {...register("qaUser", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#2d3748',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                }}
                required
              >
                <option value="">Select QA User</option>
                {qaUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
              <div className="small-helper mt-1" style={{ fontSize: '0.75rem', color: '#718096' }}>
                <i className="fas fa-info-circle mr-1"></i>
                Only QA role users are selectable
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.85rem',
                color: '#4a5568'
              }}>
                <i className="" style={{ fontSize: '0.75rem' }}></i>
                Province/State <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                {...register("province", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#2d3748',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                required
              >
                <option value="">Select Province</option>
                {provinces.map(p => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.85rem',
                color: '#4a5568'
              }}>
                <i className="" style={{ fontSize: '0.75rem' }}></i>
                City <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                {...register("city", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: watchProvince ? '#2d3748' : '#a0aec0',
                  backgroundColor: watchProvince ? '#fff' : '#f8fafc',
                  backgroundClip: 'padding-box',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                disabled={!watchProvince}
                required
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.code} value={city.code}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label label-required" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.85rem',
                color: '#4a5568'
              }}>
                <i className="" style={{ fontSize: '0.75rem' }}></i>
                Center <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                {...register("center", { required: true })}
                className="form-select form-select-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: watchCity ? '#2d3748' : '#a0aec0',
                  backgroundColor: watchCity ? '#fff' : '#f8fafc',
                  backgroundClip: 'padding-box',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                required
                disabled={!watchCity}
              >
                <option value="">Select Center</option>
                {centers.map(center => (
                  <option key={center.id} value={center.id}>
                    {center.name} ({center.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-6">
                <label className="form-label label-required" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  fontSize: '0.85rem',
                  color: '#4a5568'
                }}>
                  Visit Date <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="date"
                  {...register("visitDate", { required: true })}
                  className="form-control form-control-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#2d3748',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  required
                />
              </div>
              
              <div className="col-6">
                <label className="form-label" style={{ 
                  marginBottom: '0.5rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  fontSize: '0.85rem',
                  color: '#4a5568'
                }}>
                  Priority
                </label>
                <select
                  {...register("priority")}
                  className="form-select form-select-sm"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: '#2d3748',
                    backgroundColor: '#fff',
                    backgroundClip: 'padding-box',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <option value="">Select Priority</option>
                  {priorities?.map(p => (
                    <option key={p.id} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" style={{ 
                marginBottom: '0.5rem',
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.85rem',
                color: '#4a5568'
              }}>
                <i className="" style={{ fontSize: '0.75rem' }}></i>
                Assignment Notes
              </label>
              <textarea
                {...register("notes")}
                rows="2"
                className="form-control form-control-sm"
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: '#2d3748',
                  backgroundColor: '#fff',
                  backgroundClip: 'padding-box',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  resize: 'vertical'
                }}
                placeholder="Optional notes (e.g., coverage area, special focus)"
              />
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => reset()}
                className="btn btn-outline-gray btn-sm"
                style={{
                  border: '1px solid #e2e8f0',
                  color: '#64748b',
                  fontWeight: 500,
                  padding: '0.375rem 0.875rem',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}
              >
                <i className="fas fa-redo-alt mr-2" style={{ fontSize: '0.75rem' }}></i>
                Clear
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                style={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  border: 'none',
                  fontWeight: 500,
                  padding: '0.375rem 0.875rem',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  boxShadow: '0 1px 3px rgba(79, 70, 229, 0.3)'
                }}
              >
                <i className="fas fa-plus-circle mr-2"></i>
                Assign & Schedule
              </button>
            </div>
          </form>
        </div>

        {/* Enhanced Stats with Gradient */}
        <div className="form-section" style={{ 
          border: '1px solid #e0e6ed',
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          background: 'white',
          marginBottom: '1rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}>
          <div className="form-section-title" style={{ 
            fontWeight: 700,
            fontSize: '1rem',
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '2px solid #f1f5f9',
            paddingBottom: '0.75rem'
          }}>
            <i className="fas fa-chart-line mr-2" style={{ color: '#4f46e5' }}></i>
            Quick KPIs
          </div>
          
          <div className="row g-2">
            <div className="col-6">
              <div className="kpi" style={{
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1rem',
                height: '100%',
                color: 'white',
                boxShadow: '0 4px 6px rgba(102, 126, 234, 0.2)'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.5rem',
                  marginBottom: '0.25rem'
                }}>
                  {stats.totalAssignments}
                </div>
                <div className="label d-flex align-items-center" style={{ 
                  fontSize: '0.75rem',
                  opacity: 0.9
                }}>
                  <i className="fas fa-tasks mr-1" style={{ fontSize: '0.7rem' }}></i>
                  Total Assignments
                </div>
              </div>
            </div>
            
            <div className="col-6">
              <div className="kpi" style={{
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                padding: '1rem',
                height: '100%',
                color: 'white',
                boxShadow: '0 4px 6px rgba(240, 147, 251, 0.2)'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.5rem',
                  marginBottom: '0.25rem'
                }}>
                  {stats.totalQaUsers}
                </div>
                <div className="label d-flex align-items-center" style={{ 
                  fontSize: '0.75rem',
                  opacity: 0.9
                }}>
                  <i className="fas fa-users mr-1" style={{ fontSize: '0.7rem' }}></i>
                  QA Users
                </div>
              </div>
            </div>
            
            <div className="col-12 mt-2">
              <div className="kpi" style={{
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                padding: '1rem',
                height: '100%',
                color: 'white',
                boxShadow: '0 4px 6px rgba(79, 172, 254, 0.2)'
              }}>
                <div className="value" style={{ 
                  fontWeight: 800, 
                  fontSize: '1.5rem',
                  marginBottom: '0.25rem'
                }}>
                  {stats.totalCenters}
                </div>
                <div className="label d-flex align-items-center" style={{ 
                  fontSize: '0.75rem',
                  opacity: 0.9
                }}>
                  <i className="fas fa-university mr-1" style={{ fontSize: '0.7rem' }}></i>
                  Centers (Master)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Enhanced Assignments Table */}
      <div className="col-lg-8">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-0" style={{ fontSize: '1rem', fontWeight: 700, color: '#2c3e50' }}>
              <i className="fas fa-list-check mr-2" style={{ color: '#4f46e5' }}></i>
              Assigned Centers
            </h6>
            <div className="small-helper mt-1" style={{ fontSize: '0.75rem', color: '#718096' }}>
              Showing {filteredAssignments.length} of {assignments.length} assignments
            </div>
          </div>
          
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted" style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              <i className="fas fa-filter mr-1"></i>
              Filter by:
            </span>
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="form-select form-select-sm"
              style={{ 
                width: '220px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                padding: '0.375rem 0.75rem'
              }}
            >
              <option value="ALL">All QA Users</option>
              {qaUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive" style={{
          border: '1px solid #e0e6ed',
          borderRadius: '10px',
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          // Use flex logic or calculation for better responsiveness
          maxHeight: 'calc(100vh - 220px)', 
          minHeight: '400px', // Ensure reasonable minimum height
          overflowY: 'auto',  // Vertical scroll
          overflowX: 'auto',  // Horizontal scroll (Critical for 1200px table)
          position: 'relative',
          display: 'block' // Ensure block display for overflow to work
        }}>
          <table className="table table-sm align-middle mb-0" style={{
            minWidth: '1200px', // Ensure minimum width for all columns
            tableLayout: 'fixed' // Fixed table layout for consistent columns
          }}>
            <thead style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '2px solid #e2e8f0',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <tr>
                <th style={{ 
                  width: '50px', // Increased width for better spacing
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>#</th>
                <th style={{ 
                  width: '180px',
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px'
                }}>
                  QA User
                </th>
                <th style={{ 
                  width: '220px',
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px'
                }}>
                  Center
                </th>
                <th style={{ 
                  width: '140px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  Priority
                </th>
                <th style={{ 
                  width: '120px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  Visit Date
                </th>
                <th style={{ 
                  width: '130px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  Status
                </th>
                <th style={{ 
                  width: '120px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase',
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>
                  Assigned On
                </th>
                <th style={{ 
                  width: '200px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px'
                }}>
                  Notes
                </th>
                <th style={{ 
                  width: '120px', 
                  fontSize: '0.75rem', 
                  textTransform: 'uppercase', 
                  color: '#64748b',
                  fontWeight: 600,
                  padding: '1rem 0.75rem',
                  letterSpacing: '0.5px',
                  textAlign: 'center'
                }}>Actions</th>
              </tr>
            </thead>
            
            <tbody>
              {filteredAssignments.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center text-muted py-5" style={{ 
                    background: '#f8fafc',
                    fontSize: '0.875rem'
                  }}>
                    <div className="py-4">
                      <i className="fas fa-inbox fa-2x mb-3" style={{ color: '#cbd5e0' }}></i>
                      <div style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '0.5rem' }}>No assignments found</div>
                      <div className="small" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        {filterUser !== 'ALL' ? 'Try selecting "All QA Users" or add new assignments' : 'Start by assigning centers to QA users'}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAssignments.map((assignment, index) => (
                  <tr key={assignment.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    background: index % 2 === 0 ? '#fff' : '#fafcff',
                    transition: 'background 0.2s ease'
                  }}
                  className="hover-row"
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafd'}
                  onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafcff'}
                  >
                    <td style={{ 
                      fontSize: '0.8125rem', 
                      color: '#64748b',
                      fontWeight: 500,
                      padding: '1rem 0.75rem',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      <div style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.8rem',
                        color: '#4f46e5',
                        fontWeight: 600,
                        margin: '0 auto'
                      }}>
                        {index + 1}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      padding: '1rem 0.75rem',
                      verticalAlign: 'middle'
                    }}>
                      <div className="d-flex align-items-center">
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          marginRight: '0.75rem',
                          flexShrink: 0,
                          boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                        }}>
                          {getUserName(assignment.qaUserId, assignment.qaUserName).charAt(0)}
                        </div>
                        <div style={{ 
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            color: '#2d3748', 
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {getUserName(assignment.qaUserId, assignment.qaUserName)}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#718096',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            ID: {assignment.qaUserId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem',
                      padding: '1rem 0.75rem',
                      verticalAlign: 'middle'
                    }}>
                      <div>
                        <div style={{ 
                          fontWeight: 600, 
                          color: '#2d3748', 
                          fontSize: '0.9rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginBottom: '3px'
                        }}>
                          {getCenterName(assignment.centerId, assignment.centerName)}
                        </div>
                        {getCityName(assignment.centerId) && (
                          <div style={{ 
                            fontSize: '0.8rem', 
                            color: '#718096',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            <i className="fas fa-map-marker-alt mr-1" style={{ fontSize: '0.7rem' }}></i>
                            {getCityName(assignment.centerId)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem', 
                      padding: '1rem 0.75rem',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      {getScheduleForAssignment(assignment.qaUserId, assignment.centerId)?.priority ? (
                        getPriorityBadge(getScheduleForAssignment(assignment.qaUserId, assignment.centerId).priority)
                      ) : (
                        getPriorityBadge("Normal")
                      )}
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem',
                      color: '#475569',
                      padding: '1rem 0.75rem',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      {getScheduleForAssignment(assignment.qaUserId, assignment.centerId)?.visitDate ? (
                        <div style={{
                          padding: '6px 10px',
                          borderRadius: '6px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.8rem',
                          fontWeight: 500
                        }}>
                          {getScheduleForAssignment(assignment.qaUserId, assignment.centerId).visitDate}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                      )}
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem',
                      padding: '1rem 0.75rem',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      {getScheduleForAssignment(assignment.qaUserId, assignment.centerId) ? 
                        getStatusBadge(getScheduleForAssignment(assignment.qaUserId, assignment.centerId).status) 
                        : <span className="text-muted" style={{ fontSize: '0.8rem' }}>-</span>
                      }
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem', 
                      color: '#475569',
                      padding: '1rem 0.75rem',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      <div style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        fontSize: '0.8rem',
                        fontWeight: 500
                      }}>
                        {new Date(assignment.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ 
                      fontSize: '0.85rem', 
                      color: '#475569',
                      padding: '1rem 0.75rem',
                      verticalAlign: 'middle'
                    }}>
                      {assignment.notes ? (
                        <div style={{
                          maxWidth: '200px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          fontSize: '0.8rem',
                          lineHeight: '1.4',
                          maxHeight: '60px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {assignment.notes}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                          <i className="fas fa-minus mr-1"></i>
                          No notes
                        </span>
                      )}
                    </td>
                    <td style={{ 
                      padding: '1rem 0.75rem',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}>
                      <div className="d-flex gap-2 justify-content-center">
                        {(() => {
                          const schedule = getScheduleForAssignment(assignment.qaUserId, assignment.centerId);
                          if (!schedule) return null;


                          if (schedule.status === "In Progress") {
                            return (
                              <button
                                onClick={() => onCompleteVisit(schedule.id)}
                                className="btn btn-sm btn-success"
                                title="Complete Visit"
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  fontSize: '0.75rem',
                                  borderRadius: '6px',
                                  fontWeight: 500
                                }}
                              >
                                <i className="fas fa-check mr-1"></i>
                                Complete
                              </button>
                            );
                          }
                          // return (
                          //   <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                              
                          //   </span>
                          // );
                        })()}

                        <button
                          onClick={async () => {
                            if (window.confirm("Are you sure you want to remove this assignment?")) {
                              await onDeleteAssignment(assignment.id);
                            }
                          }}
                          className="btn btn-outline-danger btn-sm"
                          title="Remove Assignment"
                          style={{
                            border: '1px solid #fed7d7',
                            color: '#e53e3e',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '6px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fed7d7';
                            e.currentTarget.style.borderColor = '#fc8181';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#fed7d7';
                          }}
                        >
                          <i className="fas fa-trash-alt mr-1"></i>
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="small-helper mt-3 p-3" style={{ 
          fontSize: '0.75rem', 
          color: '#718096',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-info-circle mr-2 mt-1" style={{ color: '#4f46e5' }}></i>
            <div>
              <div style={{ fontWeight: 500, marginBottom: '2px' }}>Tip:</div>
              <div>Scroll vertically to view all assignments. Only assigned centers can be scheduled for a QA user.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssignSchools;